import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUI } from './UIContext';
import { controlApi } from '../services/controlApi';
import { authService } from '../services/api/auth.service';
import { clearAuthData, TOKEN_KEYS, API_BASE_URL, getStoredAccessToken, getStoredRefreshToken, persistAuthTokens } from '../services/api/api';
import { WorkspaceService } from '../services/workspace/workspace.service';
import {
  RegisterDto,
  LoginDto,
  User,
  Organization,
  AuthResponseData,
  RefreshResponseData,
} from '../services/api/auth.types';
import {
  Role,
  Permission,
  MembershipPermissions,
  resolvePermissions,
  normalizeRole,
} from '../services/permissions/permissions.service';

export interface AuthContextType {
  // Official CYZOR Auth API State
  user: User | null;
  organization: Organization | null;
  loading: boolean;
  isAuthenticated: boolean;

  // Official CYZOR Auth API Methods
  login: (dto: LoginDto) => Promise<AuthResponseData>;
  register: (dto: RegisterDto) => Promise<AuthResponseData>;
  logout: () => void;
  refresh: () => Promise<RefreshResponseData | null>;

  // Legacy / UI Helper Compatibilities
  dbUser: any;
  token: string | null;
  setActiveWorkspace: (ws: any) => void;
  workspaces: any[];
  setWorkspaces: (ws: any[]) => void;
  activeWorkspace: any;
  isSwitchingWorkspace: boolean;
  workspaceLoading: boolean;
  currentWorkspaceRole: Role | null;
  currentWorkspacePermissions: Permission[];
  selectWorkspace: (ws: any) => Promise<void>;
  reloadWorkspaceAccess: () => Promise<void>;
  tourCompleted: boolean;
  setTourCompleted: (completed: boolean) => void;
  isCreateWorkspaceModalOpen: boolean;
  setIsCreateWorkspaceModalOpen: (open: boolean) => void;
  globalBranding: any;
  refreshBranding: () => Promise<void>;
  googleCalendarToken: string | null;
  googleDriveToken: string | null;
  googleTasksToken: string | null;
  googleKeepToken: string | null;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (email: string, pass: string) => Promise<void>;
  registerWithEmail: (email: string, pass: string, fullName: string, organizationName: string) => Promise<void>;
  syncSaaSState: () => Promise<void>;
  updateSaaSBackend: (plan?: string, workspaceId?: number) => Promise<void>;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
  connectGoogleCalendar: () => Promise<string | null>;
  connectGoogleDrive: () => Promise<string | null>;
  connectGoogleTasks: () => Promise<string | null>;
  connectGoogleKeep: () => Promise<string | null>;
  disconnectGoogleCalendar: () => void;
  disconnectGoogleDrive: () => void;
  disconnectGoogleTasks: () => void;
  disconnectGoogleKeep: () => void;
  updateUserProfile?: (updates: Record<string, any>) => void;
  apiMode: 'live' | 'mock';
  setApiMode: (mode: 'live' | 'mock') => void;
  apiOnline: boolean;
  checkApiHealth: () => Promise<void>;
}

const DEV_PLATFORM_ADMIN_EMAILS = new Set(['admin@cyzor.com.br']);

function isPlatformAdminByRoleOrEmail(roles: Array<string | undefined> | undefined, email?: string | null): boolean {
  const normalizedRoles = (roles || []).map(role => String(role || '').toUpperCase());
  const hasPlatformRole = normalizedRoles.some(role => ['SUPER_ADMIN', 'ADMIN', 'PLATFORM_ADMIN'].includes(role));
  const normalizedEmail = String(email || '').trim().toLowerCase();
  return hasPlatformRole || DEV_PLATFORM_ADMIN_EMAILS.has(normalizedEmail);
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    setUser: setUiUser,
    dbUser,
    setDbUser,
    workspaces,
    setWorkspaces,
    activeWorkspace,
    setActiveWorkspace,
    isCreateWorkspaceModalOpen,
    setIsCreateWorkspaceModalOpen,
    showToast,
    setCurrentView,
  } = useUI();

  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const [apiMode, setApiModeState] = useState<'live' | 'mock'>(controlApi.getApiMode());
  const [apiOnline, setApiOnline] = useState<boolean>(true);
  const [globalBranding, setGlobalBranding] = useState<any>(null);

  // Workspace access model: role & permissions scoped to the active workspace
  const [workspaceRole, setWorkspaceRole] = useState<Role | null>(null);
  const [workspacePermissions, setWorkspacePermissions] = useState<Permission[]>([]);
const [isSwitchingWorkspace, setIsSwitchingWorkspace] = useState<boolean>(false);
  const [workspaceLoading, setWorkspaceLoading] = useState<boolean>(false);
  const sessionInitializationStarted = useRef(false);

  /**
   * Refresh the current user's membership (role + permissions) for the active workspace.
    * BACKEND DEPENDENCY: uses WorkspaceService.resolveMembership which hits
    * GET /api/workspaces/{id}/membership. Falls back to VIEWER on failure.
    * Does NOT use localStorage as an authority for permissions.
    */
   const reloadWorkspaceAccess = useCallback(async () => {
     if (!activeWorkspace?.id) {
       setWorkspaceRole(null);
       setWorkspacePermissions([]);
       return;
     }

     try {
       const membership: MembershipPermissions = await WorkspaceService.resolveMembership(
         activeWorkspace.id,
         user,
       );
       const role = normalizeRole(membership.role);
       const permissions = membership.permissions.length > 0
         ? membership.permissions
         : resolvePermissions({ role, permissions: [] });
       setWorkspaceRole(role);
       setWorkspacePermissions(permissions);
     } catch (err) {
       console.warn('Erro ao carregar membership do workspace:', err);
       const fallbackRole = normalizeRole(
         (user as any)?.role || (user as any)?.dbUser?.role,
       );
       setWorkspaceRole(fallbackRole);
       setWorkspacePermissions(resolvePermissions({ role: fallbackRole, permissions: [] }));
     }
   }, [activeWorkspace?.id, user]);

  /**
   * Switch to a different workspace and refresh the access scope (role, permissions).
   * Persists selection in sessionStorage so it survives reloads.
   */
  const selectWorkspace = useCallback(async (ws: any) => {
    if (!ws || !ws.id) return;
    setIsSwitchingWorkspace(true);
    try {
      setActiveWorkspace(ws);
      sessionStorage.setItem('active_workspace_id', String(ws.id));
      localStorage.setItem('cyzor_active_workspace_id', String(ws.id));
      await reloadWorkspaceAccess();
    } catch (err) {
      console.warn('Erro ao trocar de workspace:', err);
    } finally {
      setIsSwitchingWorkspace(false);
    }
  }, [setActiveWorkspace, reloadWorkspaceAccess]);

  const checkApiHealth = async () => {
    const health = await controlApi.checkHealth();
    setApiOnline(health.online);
  };

  const setApiMode = (mode: 'live' | 'mock') => {
    controlApi.setApiMode(mode);
    setApiModeState(mode);
    showToast(`Modo alterado para: ${mode === 'live' ? 'API de Produção (Online)' : 'Ambiente Local (Sandbox)'}`);
  };

  const resolveActiveWorkspace = useCallback((
    loadedWorkspaces: any[],
    org: Organization | null,
    storedWorkspaceId: string | null,
  ): any | undefined => {
    if (!loadedWorkspaces.length) return undefined;

    const orgWorkspaces = org?.id
      ? loadedWorkspaces.filter((ws: any) => String(ws.organizationId) === String(org.id))
      : loadedWorkspaces;

    if (!orgWorkspaces.length) return undefined;

    if (storedWorkspaceId) {
      const match = orgWorkspaces.find((ws: any) => String(ws.id) === String(storedWorkspaceId));
      if (match) return match;
    }

    return orgWorkspaces[0];
  }, []);

  // Synchronize state to localStorage and UI context
  const saveAuthSession = async (authData: AuthResponseData) => {
    if (authData.accessToken) {
      persistAuthTokens(authData.accessToken, authData.refreshToken);
    }

    if (authData.user) {
      const formattedUser: User = {
        ...authData.user,
        uid: authData.user.id,
        displayName: authData.user.fullName || authData.user.email,
        name: authData.user.fullName || authData.user.email,
        photoURL: authData.user.avatarUrl || null,
      };

      setUser(formattedUser);
      localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(formattedUser));

      const userRoles = Array.isArray(authData.roles) ? authData.roles : [];
      const effectiveRoles = isPlatformAdminByRoleOrEmail(userRoles, formattedUser.email)
        ? [...new Set([...userRoles, 'SUPER_ADMIN'])]
        : userRoles;
      const isSuperAdmin = isPlatformAdminByRoleOrEmail(effectiveRoles, formattedUser.email);

      const adaptedUiUser = {
        uid: formattedUser.id,
        email: formattedUser.email,
        displayName: formattedUser.fullName,
        photoURL: formattedUser.avatarUrl || null,
        isPlatformAdmin: isSuperAdmin,
        roles: effectiveRoles,
      };
      setUiUser(adaptedUiUser);
      setDbUser(adaptedUiUser);

      if (effectiveRoles.length > 0) {
        localStorage.setItem(TOKEN_KEYS.ROLES, JSON.stringify(effectiveRoles));
      }
    }

    if (authData.organization) {
      setOrganization(authData.organization);
      localStorage.setItem(TOKEN_KEYS.ORGANIZATION, JSON.stringify(authData.organization));
    }

    if (authData.permissions) {
      localStorage.setItem(TOKEN_KEYS.PERMISSIONS, JSON.stringify(authData.permissions));
    }

    setIsAuthenticated(true);
    setWorkspaceLoading(true);

    // Bootstrap workspaces after authentication
    // Use listAll() which handles users with memberships in multiple organizations.
    // Workspace load failure is a UX issue (empty workspaces), NOT a logout trigger.
    try {
      let loadedWorkspaces: any[] = [];
      try {
        loadedWorkspaces = await WorkspaceService.listAll();
      } catch (listAllErr) {
        console.warn('listAll workspaces failed, falling back to list():', listAllErr);
        try {
          loadedWorkspaces = await WorkspaceService.list();
        } catch (listErr) {
          console.warn('Workspace list also failed, continuing with empty workspaces:', listErr);
        }
      }

      if (loadedWorkspaces.length === 0) {
        try {
          loadedWorkspaces = await WorkspaceService.list();
        } catch (listErr) {
          console.warn('Workspace list also failed after empty listAll response:', listErr);
        }
      }

      if (loadedWorkspaces.length > 0) {
        setWorkspaces(loadedWorkspaces);
        const storedActiveWorkspaceId = sessionStorage.getItem('active_workspace_id') || localStorage.getItem('cyzor_active_workspace_id');
        const activeWs = resolveActiveWorkspace(loadedWorkspaces, authData.organization || organization, storedActiveWorkspaceId);

        if (!activeWs) {
          console.warn('Nenhum workspace pertence à organização ativa.');
          setActiveWorkspace(null);
          return;
        }

        setActiveWorkspace(activeWs);

        // Refresh membership using the resolved workspace
        try {
          const membership: MembershipPermissions = await WorkspaceService.resolveMembership(
            activeWs.id,
            authData.user,
          );
          const role = normalizeRole(membership.role);
          const permissions = membership.permissions.length > 0
            ? membership.permissions
            : resolvePermissions({ role, permissions: [] });
          setWorkspaceRole(role);
          setWorkspacePermissions(permissions);
        } catch (err) {
          console.warn('Failed to resolve membership:', err);
          const fallbackRole = normalizeRole(
            (authData.user as any)?.role || (authData.user as any)?.dbUser?.role,
          );
          setWorkspaceRole(fallbackRole);
          setWorkspacePermissions(resolvePermissions({ role: fallbackRole, permissions: [] }));
        }
      } else {
        // Workspace loading failed but auth was successful - don't logout.
        // The user can retry workspace loading later or contact admin.
        console.warn('Nenhum workspace disponível, mas autenticação é válida. O usuário pode precisar de onboarding ou o admin deve verificar as configurações de workspace.');
        setWorkspaceLoading(false);
        // NÃO setar activeWorkspace para null - isso causaria logout cascade.
        // Manter o usuário autenticado para que possa navegar ou retryar.
      }
} catch (err) {
      // Never let workspace loading cause logout cascade
      console.warn('Failed to load workspaces:', err);
      setWorkspaces([]);
      setWorkspaceLoading(false);
    }
    finally {
      setWorkspaceLoading(false);
    }
  };

  // Official Logout Method
  const logout = useCallback(() => {
    clearAuthData();
    setUser(null);
    setOrganization(null);
    setIsAuthenticated(false);
    setWorkspaceRole(null);
    setWorkspacePermissions([]);
    setUiUser(null);
    setDbUser(null);
    showToast('Sessão encerrada com segurança.');
    setCurrentView('login');
  }, [setUiUser, setDbUser, showToast, setCurrentView]);

  const logoutRef = React.useRef(logout);
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Automatic Session Recovery on App Load
  useEffect(() => {
    if (sessionInitializationStarted.current) return;
    sessionInitializationStarted.current = true;

    const initSession = async () => {
      setLoading(true);
      try {
        const accessToken = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
        const storedUser = localStorage.getItem(TOKEN_KEYS.USER);
        const storedOrg = localStorage.getItem(TOKEN_KEYS.ORGANIZATION);

        if (accessToken) {
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            const formattedUser: User = {
              ...parsedUser,
              uid: parsedUser.id || parsedUser.uid,
              displayName: parsedUser.fullName || parsedUser.displayName || parsedUser.name || parsedUser.email,
              name: parsedUser.fullName || parsedUser.name || parsedUser.displayName || parsedUser.email,
              photoURL: parsedUser.avatarUrl || parsedUser.photoURL || null,
            };
            setUser(formattedUser);
            let storedRoles: string[] = [];
            try {
              storedRoles = JSON.parse(localStorage.getItem(TOKEN_KEYS.ROLES) || '[]');
            } catch (e) {}
            const effectiveRoles = isPlatformAdminByRoleOrEmail(storedRoles, formattedUser.email)
              ? [...new Set([...storedRoles, 'SUPER_ADMIN'])]
              : storedRoles;
            const isSuperAdmin = isPlatformAdminByRoleOrEmail(effectiveRoles, formattedUser.email);

            const adaptedUiUser = {
              uid: formattedUser.id,
              email: formattedUser.email,
              displayName: formattedUser.fullName || formattedUser.displayName,
              photoURL: formattedUser.avatarUrl || formattedUser.photoURL || null,
              isPlatformAdmin: isSuperAdmin,
              roles: effectiveRoles,
            };

            localStorage.setItem(TOKEN_KEYS.ROLES, JSON.stringify(effectiveRoles));
            setUiUser(adaptedUiUser);
            setDbUser(adaptedUiUser);
          }
          if (storedOrg) {
            setOrganization(JSON.parse(storedOrg));
          }
          setIsAuthenticated(true);

          // Load workspaces after session recovery
          // Use listAll for admin / multi-org users.
          // Failure here must not cascade to logout.
          try {
            let loadedWorkspaces: any[] = [];
            try {
              loadedWorkspaces = await WorkspaceService.listAll();
            } catch (listAllErr) {
              console.warn('listAll workspaces failed in session recovery, trying list():', listAllErr);
              try {
                loadedWorkspaces = await WorkspaceService.list();
              } catch (_) {
                loadedWorkspaces = [];
              }
            }

            if (loadedWorkspaces.length === 0) {
              try {
                loadedWorkspaces = await WorkspaceService.list();
              } catch (listErr) {
                console.warn('Workspace list also failed after empty listAll response:', listErr);
              }
            }

            if (loadedWorkspaces.length > 0) {
              setWorkspaces(loadedWorkspaces);
              const storedActiveWorkspaceId = sessionStorage.getItem('active_workspace_id') || localStorage.getItem('cyzor_active_workspace_id');
              const storedOrganization = storedOrg ? JSON.parse(storedOrg) : organization;
              const activeWs = resolveActiveWorkspace(loadedWorkspaces, storedOrganization, storedActiveWorkspaceId);
              const selectedWs = activeWs || loadedWorkspaces[0];
              setActiveWorkspace(selectedWs);
              const membership: MembershipPermissions = await WorkspaceService.resolveMembership(
                selectedWs.id,
                JSON.parse(localStorage.getItem(TOKEN_KEYS.USER) || '{}'),
              );
              const role = normalizeRole(membership.role);
              const permissions = membership.permissions.length > 0
                ? membership.permissions
                : resolvePermissions({ role, permissions: [] });
              setWorkspaceRole(role);
              setWorkspacePermissions(permissions);
            }
          } catch (err) {
            console.warn('Erro ao carregar workspaces:', err);
            // Don't logout - just leave workspaces empty, user can retry
            // Never erase a workspace that was already resolved due to a transient API failure.
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Erro ao restaurar sessão de autenticação:', err);
        clearAuthData();
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    const sessionPromise = initSession();
    checkApiHealth();

    sessionPromise.then(() => {
      refreshBranding();
    }).catch(() => {
      // ignore branding load failure
    });

    const interval = setInterval(checkApiHealth, 45000);

    const handleModeChange = () => {
      setApiModeState(controlApi.getApiMode());
    };

    const handleUnauthorized = () => {
      logoutRef.current();
    };

    window.addEventListener('cyzor_api_mode_changed', handleModeChange);
    window.addEventListener('cyzor_unauthorized', handleUnauthorized);

    return () => {
      clearInterval(interval);
      window.removeEventListener('cyzor_api_mode_changed', handleModeChange);
      window.removeEventListener('cyzor_unauthorized', handleUnauthorized);
    };
  }, []);

  // Official Login Method
  const login = async (dto: LoginDto): Promise<AuthResponseData> => {
    const authData = await authService.login(dto);
    await saveAuthSession(authData);
    await refreshBranding();
    showToast('Autenticação realizada com sucesso!');
    return authData;
  };

  // Official Register Method
  const register = async (dto: RegisterDto): Promise<AuthResponseData> => {
    const authData = await authService.register(dto);
    if (authData.accessToken) {
      await saveAuthSession(authData);
      await refreshBranding();
      showToast('Conta e Organização registradas com sucesso!');
    } else {
      showToast('Cadastro realizado. Por favor, faça login.');
    }
    return authData;
  };

  // Official Refresh Method
  const refresh = async (): Promise<RefreshResponseData | null> => {
    const refreshTokenStr = getStoredRefreshToken();
    if (!refreshTokenStr) return null;

    try {
      const data = await authService.refresh({ 
        refreshToken: refreshTokenStr,
        workspaceId: activeWorkspace?.id 
      });
      if (data.accessToken) {
        persistAuthTokens(data.accessToken, data.refreshToken);
      }
      return data;
    } catch (err) {
      logout();
      return null;
    }
  };

  // Backward compatibility wrappers for UI components
  const loginWithEmail = async (email: string, pass: string) => {
    await login({ email, password: pass });
  };

  const registerWithEmail = async (
    email: string,
    pass: string,
    fullName: string,
    organizationName: string
  ) => {
    await register({ email, password: pass, fullName, organizationName });
  };

  const updateUserProfile = useCallback((updates: Record<string, any>) => {
    setUser(prev => {
      const updated = {
        ...(prev || {}),
        ...updates,
        displayName: updates.displayName || updates.fullName || prev?.displayName || prev?.name,
        photoURL: updates.photoURL || updates.avatarUrl || prev?.photoURL || prev?.avatarUrl,
      } as User;
      try {
        localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
    setDbUser(prev => ({
      ...(prev || {}),
      ...updates,
      displayName: updates.displayName || updates.fullName || prev?.displayName,
      avatarUrl: updates.photoURL || prev?.avatarUrl,
    }));
    showToast('Perfil atualizado com sucesso!');
  }, [showToast]);

  const fetchWithAuth = useCallback(async (url: string, options: RequestInit = {}) => {
    const baseUrl = API_BASE_URL;
    const targetUrl = url.startsWith('http')
      ? url
      : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;

    const buildHeaders = (token: string | null) => ({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...((options.headers as Record<string, string>) || {}),
    });

    const executeRequest = async (accessToken: string | null, retried = false): Promise<Response> => {
      const res = await fetch(targetUrl, {
        ...options,
        headers: buildHeaders(accessToken),
      });

      if (res.status === 401) {
        if (!retried) {
          const refreshData = await refresh();
          if (refreshData?.accessToken) {
            const newToken = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
            return executeRequest(newToken, true);
          }
        }

        logout();
        const err: any = new Error('Sessão expirada. Por favor, faça login novamente.');
        err.status = 401;
        throw err;
      }

      return res;
    };

    try {
      const token = getStoredAccessToken();
      const res = await executeRequest(token);
      if (!res.ok) {
        const errorBody = await res.text().catch(() => '');
        let parsed: any = null;
        try {
          parsed = JSON.parse(errorBody || '{}');
        } catch (_) {
          parsed = null;
        }

        const serverMessage =
          parsed?.message ||
          parsed?.error?.message ||
          parsed?.detail ||
          parsed?.errors?.[0]?.message ||
          errorBody ||
          res.statusText ||
          'Erro ao processar a requisição.';

        const err: any = new Error(serverMessage);
        err.status = res.status;
        err.apiResponse = parsed || errorBody || null;
        err.rawMessage = `[Cyzor API Error] ${options.method || 'GET'} ${targetUrl} - Status: ${res.status} - ${errorBody || res.statusText}`;
        throw err;
      }

      return res;
    } catch (err) {
      console.warn('Erro ao executar fetchWithAuth:', (err as any)?.message || err);
      throw err;
    }
  }, [logout, refresh]);

  const refreshBranding = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/platform/branding');
      if (!res.ok) return;
      const payload = await res.json().catch(() => null);
      const data = payload?.data ?? payload;
      if (data) {
        setGlobalBranding({
          globalLogoUrl: data.globalLogoUrl || data.logoUrl || null,
          globalIconUrl: data.globalIconUrl || data.iconUrl || null,
          globalAppName: data.globalAppName || 'CYZOR',
          loginHeroUrl: data.loginHeroUrl || null,
          globalLogoSize: data.globalLogoSize || '40',
          globalIconSize: data.globalIconSize || '20',
        });
      }
    } catch (err) {
      console.warn('Erro ao atualizar branding global:', err);
    }
  }, [fetchWithAuth]);

  const value: AuthContextType = useMemo(() => ({
    // Official Auth API state & methods
    user,
    organization,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refresh,

    // Legacy & helper properties
    dbUser,
    token: getStoredAccessToken(),
     workspaces,
    setWorkspaces,
    activeWorkspace,
    setActiveWorkspace,
isSwitchingWorkspace,
  workspaceLoading,
  currentWorkspaceRole: workspaceRole,
    currentWorkspacePermissions: workspacePermissions,
    selectWorkspace,
    reloadWorkspaceAccess,
    tourCompleted: true,
    setTourCompleted: () => {},
    isCreateWorkspaceModalOpen,
    setIsCreateWorkspaceModalOpen,
    globalBranding,
    refreshBranding,
    googleCalendarToken: null,
    googleDriveToken: null,
    googleTasksToken: null,
    googleKeepToken: null,
    loginWithGoogle: async () => {
      showToast('Login via Google em desenvolvimento');
    },
    loginWithEmail,
    registerWithEmail,
    syncSaaSState: async () => {},
    updateSaaSBackend: async () => {
      showToast('Atualizado com sucesso!');
    },
    fetchWithAuth,
    connectGoogleCalendar: async () => 'mock-token',
    connectGoogleDrive: async () => 'mock-token',
    connectGoogleTasks: async () => 'mock-token',
    connectGoogleKeep: async () => 'mock-token',
    disconnectGoogleCalendar: () => showToast('Desconectado'),
    disconnectGoogleDrive: () => showToast('Desconectado'),
    disconnectGoogleTasks: () => showToast('Desconectado'),
    disconnectGoogleKeep: () => showToast('Desconectado'),
    updateUserProfile,
    apiMode,
    setApiMode,
    apiOnline,
    checkApiHealth,
  }), [
    user,
    organization,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refresh,
     dbUser,
    workspaces,
    setWorkspaces,
    activeWorkspace,
    setActiveWorkspace,
    isSwitchingWorkspace,
    workspaceRole,
    workspacePermissions,
    selectWorkspace,
    reloadWorkspaceAccess,
    isCreateWorkspaceModalOpen,
    setIsCreateWorkspaceModalOpen,
    globalBranding,
    refreshBranding,
    loginWithEmail,
    registerWithEmail,
    updateUserProfile,
    fetchWithAuth,
    showToast,
    apiMode,
    setApiMode,
    apiOnline,
    checkApiHealth,
    workspaceLoading,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export function useAuth() {
  return useAuthContext();
}

